/**
 * WMS Dashboard Mock Data Service
 *
 * Mock-fallback for widgets når API-kall feiler under utvikling.
 * Brukes kun i try/catch-blokker i de gjenværende widgets.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import {
  OrderSummary,
  PickingStats,
  ShippingStats,
  OperatorActivity,
  ActivityEvent,
  KPIData,
} from './types';

// Helper to add variance to numbers for realism
const withVariance = (base: number, variance: number): number => {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
};

// Generate random ID
const generateId = (): string => Math.random().toString(36).substring(2, 11);

/**
 * Get order summary data
 */
export const getOrderSummary = (): OrderSummary => ({
  pending: withVariance(23, 5),
  inProgress: withVariance(15, 3),
  completed: withVariance(142, 20),
  delayed: withVariance(3, 2),
  todayTotal: withVariance(183, 25),
  trend: Math.random() > 0.3 ? 'up' : Math.random() > 0.5 ? 'down' : 'stable',
  trendValue: withVariance(12, 5),
});

/**
 * Get picking statistics
 */
export const getPickingStats = (): PickingStats => ({
  activePickers: withVariance(8, 2),
  ordersInPicking: withVariance(12, 3),
  linesPickedToday: withVariance(1847, 200),
  avgPickTime: withVariance(18, 5),
  pickRate: withVariance(156, 20),
  efficiency: Math.min(100, withVariance(94, 5)),
});

/**
 * Get shipping statistics
 */
export const getShippingStats = (): ShippingStats => ({
  readyToShip: withVariance(28, 8),
  shippedToday: withVariance(89, 15),
  pendingLabels: withVariance(12, 5),
  avgShipTime: withVariance(8, 3),
  onTimeRate: Math.min(100, withVariance(96, 3)),
});

/**
 * Get operator activities
 */
export const getOperatorActivities = (): OperatorActivity[] => {
  const operators = [
    { id: 'Lager1', name: 'Bjørn Hansen' },
    { id: 'Lager2', name: 'Klaus Olsen' },
    { id: 'Lager4', name: 'Aleksander Berg' },
    { id: 'Lager5', name: 'Ilir Berisha' },
    { id: 'Lager6', name: 'Martin Johansen' },
    { id: 'Lager7', name: 'Mads Eriksen' },
    { id: 'Lager8', name: 'Stein Runo' },
    { id: 'Lager9', name: 'Gill Mohammed' },
    { id: 'Lager10', name: 'Kim Andersen' },
    { id: 'Lager11', name: 'Tommy Larsen' },
  ];

  const tasks: { type: OperatorActivity['taskType']; task: string }[] = [
    { type: 'picking', task: 'Plukker ordre #4523' },
    { type: 'picking', task: 'Plukker ordre #4524' },
    { type: 'picking', task: 'Batch-plukk (5 ordrer)' },
    { type: 'receiving', task: 'Mottar leveranse fra Supplier AS' },
    { type: 'receiving', task: 'Mottar retur #R-2341' },
    { type: 'putaway', task: 'Plasserer varer i sone A' },
    { type: 'putaway', task: 'Cross-docking til sone B' },
    { type: 'transfer', task: 'Internflytting til pallplass' },
    { type: 'idle', task: 'Ledig' },
  ];

  // Return random subset of active operators
  const numActive = withVariance(6, 2);
  const shuffled = [...operators].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, numActive).map((op) => {
    const taskInfo = tasks[Math.floor(Math.random() * tasks.length)];
    return {
      operatorId: op.id,
      operatorName: op.name,
      currentTask: taskInfo.task,
      taskType: taskInfo.type,
      performance: Math.min(150, Math.max(50, withVariance(100, 25))),
      linesProcessed: withVariance(180, 60),
    };
  });
};

/**
 * Get recent activity events
 */
export const getRecentActivities = (): ActivityEvent[] => {
  const types: ActivityEvent['type'][] = ['pick', 'receive', 'putaway', 'transfer', 'adjustment'];
  const operators = ['Bjørn', 'Klaus', 'Martin', 'Mads', 'Kim', 'Tommy', 'Ilir'];
  const locations = ['A1-2-3', 'B2-1-4', 'C3-4-1', 'D1-1-2', 'A4-3-2', 'B1-2-1', 'C2-3-4'];
  const items = ['SKU-001', 'SKU-042', 'SKU-108', 'SKU-234', 'SKU-567', 'SKU-891'];

  const descriptions: Record<ActivityEvent['type'], string[]> = {
    pick: ['Plukket til ordre', 'Batch-plukk fullført', 'Delplukk utført'],
    receive: ['Vare mottatt', 'Leveranse registrert', 'Retur mottatt'],
    putaway: ['Vare plassert', 'Plassering fullført', 'Cross-dock utført'],
    transfer: ['Vare flyttet', 'Intern overføring', 'Pallflytting'],
    adjustment: ['Beholdning justert', 'Korreksjon utført', 'Telling oppdatert'],
  };

  const events: ActivityEvent[] = [];
  const now = new Date();

  for (let i = 0; i < 15; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const timestamp = new Date(now.getTime() - i * withVariance(180000, 60000)); // ~3 min apart

    events.push({
      id: generateId(),
      timestamp,
      type,
      operator: operators[Math.floor(Math.random() * operators.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      itemNo: items[Math.floor(Math.random() * items.length)],
      qty: withVariance(5, 3),
      description: descriptions[type][Math.floor(Math.random() * descriptions[type].length)],
    });
  }

  return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

/**
 * Get KPI data
 */
export const getKPIs = (): KPIData[] => [
  {
    name: 'Plukkeffektivitet',
    value: Math.min(100, withVariance(94, 4)),
    target: 95,
    unit: '%',
    trend: Math.random() > 0.5 ? 'up' : 'stable',
    trendValue: withVariance(2, 1),
  },
  {
    name: 'Ordre levert i tide',
    value: Math.min(100, withVariance(97, 2)),
    target: 98,
    unit: '%',
    trend: Math.random() > 0.3 ? 'up' : 'down',
    trendValue: withVariance(1, 0.5),
  },
  {
    name: 'Plukkfeil',
    value: Math.max(0, withVariance(0.8, 0.3)),
    target: 0.5,
    unit: '%',
    trend: Math.random() > 0.5 ? 'down' : 'stable',
    trendValue: withVariance(0.1, 0.05),
  },
  {
    name: 'Lagersnurr',
    value: withVariance(12, 2),
    target: 15,
    unit: 'x/år',
    trend: 'up',
    trendValue: withVariance(0.5, 0.2),
  },
  {
    name: 'Linjer per time',
    value: withVariance(156, 20),
    target: 150,
    unit: 'linjer',
    trend: Math.random() > 0.4 ? 'up' : 'stable',
    trendValue: withVariance(8, 3),
  },
  {
    name: 'Mottak til hylle',
    value: withVariance(45, 10),
    target: 30,
    unit: 'min',
    trend: Math.random() > 0.6 ? 'down' : 'stable',
    trendValue: withVariance(3, 1),
  },
];
